// How a deck looks: its style's colours and type, worked out once as plain values (never the page's own theme
// variables), so a slide draws the same in the editor, full screen, the presenter's window, a printed page and a
// PowerPoint file. The board's own style takes the board's theme; the others are fixed palettes, each with one accent,
// which is moved until it reads on the slide (3:1, for type this large).

import { DECK_STYLE, TYPE, type DeckData, type SlideLayout } from "../../../shared/slides";
import { FONT, fontStack, type Palette, type SlideBg } from "../../../shared/slideDesign";
import { mixHex, type DeckChrome, type DeckTheme, type SavedLayout } from "../../../shared/deckTheme";
import type { ThemeSettings } from "../../../shared/types";
import type { DeckMaster, MasterKey } from "../../../shared/slideMaster";
import { FONT_VOICES, contrast, ensureContrast, readableOn, resolveTheme, withAlpha } from "../../themes/themes";

export const SANS = "'Geist Variable', 'Geist', ui-sans-serif, system-ui, sans-serif";
export const SERIF = "'Instrument Serif', Georgia, 'Times New Roman', serif";

export interface DeckLook {
  bg: string;
  fg: string;
  fg2: string;
  fg3: string;
  line: string;
  accent: string;
  /** words on the accent */
  onAccent: string;
  titleFont: string;
  bodyFont: string;
  titleWeight: number;
  titleTracking: string;
  /** a serif's titles a touch larger, to read at the weight of a sans */
  titleScale: number;
  dark: boolean;
  /** Bold: the title and section slides on the accent itself */
  keyOnAccent: boolean;
  /** what a PowerPoint file names for the fonts: ones every copy of PowerPoint has */
  pptTitleFont: string;
  pptBodyFont: string;
  /** the deck's theme colours, each as #RRGGBB (shared/slideDesign.ts THEME_COLORS): what a box's colour names */
  palette: Palette;
  /** a theme of the user's own (S6): the background every slide starts with, what goes on every slide, its layouts */
  deckBg?: SlideBg | null;
  chrome?: DeckChrome | null;
  layouts?: SavedLayout[];
  /** the theme's own font ids, for the editor and the PowerPoint file */
  fontIds?: { title: string; body: string };
  /** whether the look is a theme of the user's own rather than a built-in style */
  custom?: boolean;
  /** SC: the deck's slide master (shared/slideMaster.ts), and the piece the master view draws from the slide it edits */
  master?: DeckMaster | null;
  masterSkip?: MasterKey;
}

/** Each style's other colours: a surface a step off its ground, and three accents that sit with its own. */
const EXTRA: Record<Exclude<DeckData["style"], "board">, { surface: string; accent2: string; accent3: string; accent4: string }> = {
  paper: { surface: "#F0ECE4", accent2: "#1F6F78", accent3: "#B8862B", accent4: "#6B5B95" },
  night: { surface: "#1F2127", accent2: "#5FB3A8", accent3: "#E07A5F", accent4: "#8FA7D9" },
  bold: { surface: "#F1F3F7", accent2: "#FF5A36", accent3: "#12A37A", accent4: "#FFB627" },
  editorial: { surface: "#EAE3D5", accent2: "#3D5A4C", accent3: "#B98C2F", accent4: "#2E4A6B" },
};

const hex = (rgb: [number, number, number]) => "#" + rgb.map((c) => Math.round(c).toString(16).padStart(2, "0")).join("");

/** A colour that may be written as rgba(), as a hex one over its ground. */
function solid(c: string, over: string): string {
  const m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)(?:[\s,/]+([\d.]+))?\s*\)$/.exec(c.trim());
  if (!m) return /^#[0-9a-f]{6}$/i.test(c) ? c : over;
  const a = m[4] === undefined ? 1 : Number(m[4]);
  const g = /^#([0-9a-f]{6})$/i.exec(over);
  const base = g ? [parseInt(g[1].slice(0, 2), 16), parseInt(g[1].slice(2, 4), 16), parseInt(g[1].slice(4, 6), 16)] : [0, 0, 0];
  return hex([0, 1, 2].map((i) => Number(m[i + 1]) * a + base[i] * (1 - a)) as [number, number, number]);
}

export function deckLook(deck: DeckData, theme: ThemeSettings | null | undefined): DeckLook {
  if (deck.theme) return { ...themeLook(deck.theme), layouts: deck.layouts ?? [], master: deck.master ?? null };
  return { ...styleLook(deck, theme), layouts: deck.layouts ?? [], master: deck.master ?? null };
}

function styleLook(deck: DeckData, theme: ThemeSettings | null | undefined): DeckLook {
  const style = DECK_STYLE[deck.style] ?? DECK_STYLE.board;
  const base = (bg: string, fg: string, fg2: string, fg3: string, dark: boolean) => ({ bg, fg, fg2, fg3, dark, line: withAlpha(fg, dark ? 0.16 : 0.14) });
  let look: Omit<DeckLook, "accent" | "onAccent" | "palette">;
  let extra: { surface: string; accent2: string; accent3: string; accent4: string };
  let accent = deck.accent ?? style.accent ?? "#2F54EB";
  switch (deck.style) {
    case "paper":
      extra = EXTRA.paper;
      look = { ...base("#FAF8F4", "#1D1C1A", "#55514A", "#8E897F", false), titleFont: SANS, bodyFont: SANS, titleWeight: 640, titleTracking: "-0.03em", titleScale: 1, keyOnAccent: false, pptTitleFont: "Arial", pptBodyFont: "Arial" };
      break;
    case "night":
      extra = EXTRA.night;
      look = { ...base("#141519", "#F2F1EE", "#B8B6B0", "#7E7C77", true), titleFont: SANS, bodyFont: SANS, titleWeight: 620, titleTracking: "-0.03em", titleScale: 1, keyOnAccent: false, pptTitleFont: "Arial", pptBodyFont: "Arial" };
      break;
    case "bold":
      extra = EXTRA.bold;
      look = { ...base("#FFFFFF", "#0F1115", "#474B54", "#8A8F99", false), titleFont: SANS, bodyFont: SANS, titleWeight: 700, titleTracking: "-0.04em", titleScale: 1, keyOnAccent: true, pptTitleFont: "Arial", pptBodyFont: "Arial" };
      break;
    case "editorial":
      extra = EXTRA.editorial;
      look = { ...base("#F4EFE6", "#25211B", "#5B5448", "#918876", false), titleFont: SERIF, bodyFont: SANS, titleWeight: 400, titleTracking: "-0.012em", titleScale: 1.14, keyOnAccent: false, pptTitleFont: "Georgia", pptBodyFont: "Arial" };
      break;
    default: {
      // the board's own: its tile colour as the slide, its inks, its accent and its voice
      const r = resolveTheme(theme);
      const bg = hex(r.def.tile);
      const dark = r.def.mode !== "light";
      const voice = FONT_VOICES.find((f) => f.id === (theme?.font ?? r.def.font)) ?? FONT_VOICES[0];
      const fg = solid(r.vars["--fg"], bg);
      const serif = voice.id === "editorial";
      look = {
        ...base(bg, fg, solid(r.vars["--fg-2"], bg), solid(r.vars["--fg-3"], bg), dark),
        titleFont: voice.display,
        bodyFont: SANS,
        titleWeight: serif ? 400 : voice.id === "clean" ? 640 : Math.max(500, voice.weight),
        titleTracking: serif ? "-0.012em" : voice.id === "pixel" ? "0" : "-0.03em",
        titleScale: serif ? 1.14 : 1,
        keyOnAccent: false,
        pptTitleFont: serif ? "Georgia" : voice.id === "mono" ? "Consolas" : "Arial",
        pptBodyFont: "Arial",
      };
      if (!deck.accent) accent = solid(r.vars["--accent"], bg);
      // the board's own: its chart colours as the other accents, its tile a step lighter or darker as the surface
      const chart = (i: number, or: string) => solid(r.vars[`--chart-${i}`] ?? or, bg);
      extra = { surface: shade(bg, dark ? 0.07 : -0.045), accent2: chart(2, "#5FB3A8"), accent3: chart(3, "#E07A5F"), accent4: chart(4, "#8FA7D9") };
    }
  }
  // the accent reads on the slide: moved toward the ink until it does
  const reads = ensureContrast(accent, look.bg, 3, look.dark);
  const palette: Palette = { background: look.bg, surface: extra.surface, text: look.fg, muted: look.fg2, accent: reads, accent2: extra.accent2, accent3: extra.accent3, accent4: extra.accent4 };
  return { ...look, accent: reads, onAccent: readableOn(accent) === "#ffffff" ? "#FFFFFF" : "#0D0E11", palette };
}

/** A hex colour a touch lighter (up) or darker (down). */
function shade(c: string, by: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(c);
  if (!m) return c;
  const n = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  return hex(n.map((x) => Math.max(0, Math.min(255, by > 0 ? x + (255 - x) * by : x * (1 + by)))) as [number, number, number]);
}

/**
 * The theme colours one slide's boxes use: the deck's, with the slide's own ground and inks (a Bold title slide stands
 * on the accent, so its text is the ink that reads there).
 */
export function slidePalette(look: DeckLook, layout: SlideLayout): Palette {
  const c = slideColors(look, layout);
  if (c.bg === look.bg) return look.palette;
  return { ...look.palette, background: c.bg, text: c.fg, muted: solid(c.fg2, c.bg), accent: c.accent };
}

/** The colours one slide wears: the Bold style's title and section slides stand on the accent itself. */
export function slideColors(look: DeckLook, layout: SlideLayout, accentRaw?: string): { bg: string; fg: string; fg2: string; fg3: string; accent: string; line: string; dark: boolean } {
  if (look.keyOnAccent && (layout === "title" || layout === "section")) {
    const bg = accentRaw ?? look.accent;
    const fg = readableOn(bg) === "#ffffff" ? "#FFFFFF" : "#0D0E11";
    const dark = fg === "#FFFFFF";
    return { bg, fg, fg2: withAlpha(fg, 0.82), fg3: withAlpha(fg, 0.62), accent: fg, line: withAlpha(fg, 0.3), dark };
  }
  return { bg: look.bg, fg: look.fg, fg2: look.fg2, fg3: look.fg3, accent: look.accent, line: look.line, dark: look.dark };
}

/** The variables a slide's face sets: its colours, its type, and the type scale (shared/slides.ts TYPE), so CSS and the fit estimate agree. */
export function faceVars(look: DeckLook, layout: SlideLayout, accentRaw?: string): Record<string, string> {
  const c = slideColors(look, layout, accentRaw);
  const v: Record<string, string> = {
    "--sl-bg": c.bg,
    "--sl-fg": c.fg,
    "--sl-fg2": c.fg2,
    "--sl-fg3": c.fg3,
    "--sl-accent": c.accent,
    "--sl-line": c.line,
    "--sl-title-font": look.titleFont,
    "--sl-body-font": look.bodyFont,
    "--sl-title-weight": String(look.titleWeight),
    "--sl-title-tracking": look.titleTracking,
    "--sl-title-scale": String(look.titleScale),
    colorScheme: c.dark ? "dark" : "light",
  };
  for (const [k, step] of Object.entries(TYPE)) {
    v[`--t-${k}`] = `${step.size}px`;
    v[`--l-${k}`] = `${step.line}px`;
  }
  // the theme colours by name, for words coloured in a box's editor (S5)
  for (const [k, c2] of Object.entries(slidePalette(look, layout))) v[`--c-${k}`] = c2;
  // a widget on a slide wears the slide's colours, so it reads as part of it
  v["--tile-rgb"] = rgbTriplet(c.bg, c.dark ? 0.06 : -0.035);
  v["--tile-alpha"] = "1";
  v["--fg"] = c.fg;
  v["--fg-2"] = c.fg2;
  v["--fg-3"] = c.fg3;
  v["--accent"] = c.accent;
  v["--chart-1"] = c.accent;
  v["--line"] = withAlphaAny(c.fg, 0.08);
  v["--line-2"] = withAlphaAny(c.fg, 0.14);
  v["--tile-border"] = withAlphaAny(c.fg, c.dark ? 0.12 : 0.1);
  return v;
}

/** A hex colour as "r g b", a touch lighter (up) or darker (down), for a widget's tile on the slide. */
function rgbTriplet(c: string, shift: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(c);
  if (!m) return "128 128 128";
  const n = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  return n.map((x) => Math.round(Math.max(0, Math.min(255, shift > 0 ? x + (255 - x) * shift : x * (1 + shift))))).join(" ");
}

const withAlphaAny = (c: string, a: number) => (/^#[0-9a-f]{6}$/i.test(c) ? withAlpha(c, a) : `color-mix(in srgb, ${c} ${Math.round(a * 100)}%, transparent)`);

// ---------------------------------------------------------------------------
// a theme of the user's own (S6): its colours exactly as chosen, its two fonts, its background and every-slide items

/** How a title is set in a font: its weight, its tracking and a touch larger for a serif or a narrow display face. */
export function titleSetting(fontId: string): { weight: number; tracking: string; scale: number } {
  const f = FONT[fontId] ?? FONT.geist;
  const [lo, hi] = f.weights;
  const clamp = (w: number) => Math.max(lo, Math.min(hi, w));
  switch (f.kind) {
    case "serif":
      return { weight: clamp(lo >= 400 && hi === lo ? lo : 500), tracking: "-0.012em", scale: hi === lo ? 1.14 : 1.04 };
    case "display":
      return fontId === "bebas-neue" ? { weight: 400, tracking: "0.01em", scale: 1.2 } : { weight: clamp(700), tracking: "-0.02em", scale: 1 };
    case "mono":
      return { weight: clamp(600), tracking: "-0.02em", scale: 0.94 };
    case "hand":
      return { weight: clamp(600), tracking: "0", scale: 1.16 };
    default:
      return { weight: clamp(640), tracking: "-0.03em", scale: 1 };
  }
}

/** A theme of the user's own as the look a slide is drawn in. Its colours are kept exactly; the editor warns where they read poorly. */
export function themeLook(t: DeckTheme): DeckLook {
  const c = t.colors;
  const dark = isDark(c.background);
  const set = titleSetting(t.fonts.title);
  const fonts = { title: fontStack(t.fonts.title, { title: SANS, body: SANS }), body: fontStack(t.fonts.body, { title: SANS, body: SANS }) };
  return {
    bg: c.background,
    fg: c.text,
    fg2: c.muted,
    fg3: mixHex(c.muted, c.background, 0.3),
    line: withAlpha(c.text, dark ? 0.16 : 0.14),
    accent: c.accent,
    onAccent: readableOn(c.accent) === "#ffffff" ? "#FFFFFF" : "#0D0E11",
    titleFont: fonts.title,
    bodyFont: fonts.body,
    titleWeight: set.weight,
    titleTracking: set.tracking,
    titleScale: set.scale,
    dark,
    keyOnAccent: t.key === "accent",
    pptTitleFont: FONT[t.fonts.title]?.name ?? "Arial",
    pptBodyFont: FONT[t.fonts.body]?.name ?? "Arial",
    palette: { ...c },
    deckBg: t.bg ?? null,
    chrome: t.chrome ?? null,
    fontIds: { ...t.fonts },
    custom: true,
  };
}

const isDark = (c: string) => contrast(c, "#000000") < contrast(c, "#ffffff");

/** The font a built-in style's titles and words are in, as a font of the list: where Customise starts from. */
function styleFonts(look: DeckLook): { title: string; body: string } {
  const t = look.titleFont;
  const title = /Instrument Serif/.test(t) ? "instrument-serif" : /JetBrains/.test(t) ? "jetbrains-mono" : "geist";
  return { title, body: "geist" };
}

/** A built-in style's look as a theme of one's own, to start the editor from ("Customise"). */
export function themeOfLook(look: DeckLook, name: string): DeckTheme {
  if (look.custom && look.fontIds) return { name, colors: { ...look.palette }, fonts: { ...look.fontIds }, ...(look.deckBg ? { bg: look.deckBg } : {}), ...(look.keyOnAccent ? { key: "accent" as const } : {}), ...(look.chrome ? { chrome: look.chrome } : {}) };
  const colors = Object.fromEntries(Object.entries(look.palette).map(([k, v]) => [k, v.toUpperCase()])) as Palette;
  return { name, colors, fonts: styleFonts(look), ...(look.keyOnAccent ? { key: "accent" as const } : {}), chrome: { number: "br", skip: "key" } };
}
